/**
 * Every read `/konto` makes, as `queryOptions`.
 *
 * `GET /personaltrainer/{id}` serves BOTH the Profil form and the trainer-code card — one cache
 * entry, two projections. `/auth/me` is not used for it: that response carries no `trainerCode`, so
 * the tab would need this read anyway, and a form seeded from one source and saved against another
 * is how the two come to disagree.
 *
 * The brand colour's read is not here: the app shell paints with it too, so it lives in
 * `entities/brand-color` and this screen shares that one cache entry. The Google connection's
 * likewise lives in `entities/google-calendar-connection`: `/rezerwacje` reads it too.
 *
 * None of them states a `staleTime`: the app's own window stands, and every write on this
 * screen invalidates what it changed. A zero window turned each tab click into a request.
 */

import { queryOptions } from '@tanstack/react-query';

import { getMeEntitlements } from '~/shared/api/generated/endpoints/entitlement/entitlement';
import { getMePlans } from '~/shared/api/generated/endpoints/plan-catalog/plan-catalog';
import { getPersonaltrainerId } from '~/shared/api/generated/endpoints/personal-trainer/personal-trainer';
import { getMeSubscriptionWebTrial } from '~/shared/api/generated/endpoints/web-checkout/web-checkout';

/** The prefix every read on this screen shares. Invalidate it to drop the whole screen's cache. */
export const accountRoot = ['account'] as const;

export function trainerProfileQueryKey(trainerId: number) {
  return [...accountRoot, 'trainer', trainerId] as const;
}

/**
 * `GET /personaltrainer/{id}` — the trainer's own row: names, the sign-in address, the code.
 *
 * A DISABLED query reports `isPending` for ever, so a gate reading `isPending` alone draws a
 * skeleton that never retires. The caller's gate carries the enablement predicate beside it.
 */
export function trainerProfileQueryOptions(trainerId: number | null) {
  return queryOptions({
    queryKey: trainerProfileQueryKey(trainerId ?? 0),
    queryFn: ({ signal }) => getPersonaltrainerId(trainerId ?? 0, undefined, signal),
    enabled: trainerId !== null,
  });
}

export const entitlementsQueryKey = [...accountRoot, 'entitlements'] as const;

/**
 * `GET /me/entitlements` — the plan, its renewal, its cap and any queued change.
 *
 * `activeClientLimit` here is the CAP and says nothing about occupancy: every SEAT number comes
 * from `entities/client`'s `seatUsageQueryOptions`, which is the one source of truth for seats.
 *
 * The one thing that changes this answer happens in another application entirely — a trainer
 * manages the subscription in the App Store and comes back. That is the browser's own focus event,
 * which TanStack's focus manager already listens for; nothing here subscribes to it by hand.
 */
export function entitlementsQueryOptions() {
  return queryOptions({
    queryKey: entitlementsQueryKey,
    queryFn: ({ signal }) => getMeEntitlements(undefined, signal),
  });
}

export const planCatalogQueryKey = [...accountRoot, 'plans'] as const;

/**
 * `GET /me/plans` — every level this account can be on, with the web channel's price on the rows
 * that carry one.
 *
 * It answers the CATALOGUE, never the entitlement: which plan the account is on comes from
 * `entitlementsQueryOptions` above, and the picker joins the two. A card built from this read alone
 * would have no current plan to mute.
 *
 * The price is the provider's and is cached for five minutes on the server; it arrives null while
 * the provider is unavailable, which `model/webOffers` drops rather than draws as an empty amount.
 */
export function planCatalogQueryOptions() {
  return queryOptions({
    queryKey: planCatalogQueryKey,
    queryFn: ({ signal }) => getMePlans(undefined, signal),
  });
}

export const webTrialQueryKey = [...accountRoot, 'web-trial'] as const;

/**
 * `GET /me/subscription/web/trial` — a running web trial's end and what it will charge (R19), read
 * live from the provider.
 *
 * Asked for a web subscription only, and never retried: requirement R18 makes the plain renewal row
 * the answer whenever the provider does not answer, so a failure is a fallback rather than three
 * more attempts holding the tab's skeleton up. Entitlement never depends on it.
 */
export function webTrialQueryOptions() {
  return queryOptions({
    queryKey: webTrialQueryKey,
    queryFn: ({ signal }) => getMeSubscriptionWebTrial(undefined, signal),
    retry: false,
  });
}
